import React, { type FC, Fragment } from 'react'
import { Box, Divider, type SxProps, type Theme } from '@mui/material'

import { executeOnce } from '../../../utils'

import { selectSideNavCollapsed } from '../../../domain'

import { SidePanelCloseIcon } from '../common'
import { AbsoluteRouteOnlyPath, useRouting } from '../routing'
import { useAppState, useAppUsecase } from '../State'
import { useNavigationFormatter } from './Hooks'
import { NavigationPermissionedLink } from './Link'
import { useNavigationRouting } from './NavigationRoutingHook'

type Props = Readonly<{ route: AbsoluteRouteOnlyPath }>

const SIDEBAR_ANIMATION_CSS = '.25s ease-in-out'

const linkStyle: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
    fontSize: 'body2.fontSize',
    borderRadius: 1,
    p: 2.5,
    mb: 3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    '&:hover': { backgroundColor: 'grey.100' },
}
const selectedLinkStyle: SxProps<Theme> = { color: 'primary.700', backgroundColor: 'grey.200' }

const iconStyle: SxProps<Theme> = { mr: 3, width: 20, height: 20, fill: 'none' }
const selectedIconStyle: SxProps<Theme> = { color: 'primary.700' }

const NavigationLink: FC<Props> = ({ route }) => {
    const routing = useRouting()
    const formatter = useNavigationFormatter()
    const collapsed = useAppState(selectSideNavCollapsed)

    const selected = routing.isAtRoute(route)
    const Icon = formatter.formatIcon(route)

    return (
        <NavigationPermissionedLink path={route} withTooltip={collapsed} sx={[linkStyle, selected ? selectedLinkStyle : null]}>
            <Icon sx={[iconStyle, selected ? selectedIconStyle : null]} />
            {formatter.formatTitle(route)}
        </NavigationPermissionedLink>
    )
}

const listStyle: SxProps<Theme> = {
    my: 4,
    mx: 2,
    '& > a:last-child': {
        mb: 0,
    },
}
const NavigationLinkList: FC<{ routes: readonly AbsoluteRouteOnlyPath[] }> = ({ routes, ...props }) => (
    <Box sx={listStyle} {...props}>
        {routes.map((route) => (
            <NavigationLink key={route} route={route} />
        ))}
    </Box>
)

const getAllRouteOnlyPaths = executeOnce(() => Object.values(AbsoluteRouteOnlyPath))
const drawerStyle: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: 300,
    transition: `width ${SIDEBAR_ANIMATION_CSS}`,
    position: 'relative',
}
const collapsedDrawerStyle: SxProps<Theme> = { width: 56 }
const dividerStyle: SxProps<Theme> = { mx: 1 }
const closeIconStyle: SxProps<Theme> = {
    width: 24,
    height: 24,
    m: 4,
    color: 'grey.500',
    position: 'absolute',
    bottom: 0,
    right: 0,
    cursor: 'pointer',
    transition: `transform ${SIDEBAR_ANIMATION_CSS}`,
}
const collapsedCloseIconStyle: SxProps<Theme> = { transform: 'scaleX(-1)' }

export const SideNavigation: FC = () => {
    const routing = useNavigationRouting()
    const groups = routing.getSideMenuGroups(getAllRouteOnlyPaths())

    const navigationUsecase = useAppUsecase('navigationUsecase')
    const collapsed = useAppState(selectSideNavCollapsed)

    return (
        <Box sx={[drawerStyle, collapsed && collapsedDrawerStyle]} data-testid="side-navigation">
            {groups.map((group, k) => {
                if (group.length === 0) {
                    return null
                }

                return (
                    <Fragment key={k}>
                        {k > 0 && <Divider data-testid="side-navigation-divider" sx={dividerStyle} />}
                        <NavigationLinkList data-testid={`side-navigation-${k + 1}`} routes={group} />
                    </Fragment>
                )
            })}
            <SidePanelCloseIcon onClick={() => navigationUsecase.toggleSideNavCollapsed()} sx={[closeIconStyle, collapsed && collapsedCloseIconStyle]} />
        </Box>
    )
}
